import { useNavigate } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import { Avatar, Badge, Button, Drawer, StatusChip } from "@/components/ui";
import type { UserListItemDto } from "../types";
import { accountStatusTone, formatDate, formatLabel, tierName } from "../labels";

export interface UserPreviewDrawerProps {
  open: boolean;
  user: UserListItemDto | null;
  onClose: () => void;
}

/** Related-record style preview — material work stays on User Detail. */
export function UserPreviewDrawer({
  open,
  user,
  onClose,
}: UserPreviewDrawerProps) {
  const navigate = useNavigate();
  const play = user?.play ?? null;

  return (
    <Drawer open={open} onClose={onClose} title="User preview">
      {user ? (
        <Stack gap={16}>
          <Flex gap={12} align="center">
            <Avatar
              name={play?.name || user.display_name || "?"}
              {...(play?.avatar ? { src: play.avatar } : {})}
              style={{ width: 56, height: 56 }}
            />
            <Stack gap={4}>
              <Text role="title" as="h3">
                {play?.name || user.display_name || "Unnamed User"}
              </Text>
              {play ? (
                <Text role="small" color="secondary" as="span">
                  @{play.handle}
                </Text>
              ) : null}
            </Stack>
          </Flex>
          <Flex gap={8} wrap>
            <StatusChip tone={accountStatusTone(user.account_status)}>
              {formatLabel(user.account_status)}
            </StatusChip>
            {play?.verified ? <Badge tone="success">Verified</Badge> : null}
            {play ? <Badge tone="neutral">{tierName(play.tier)} tier</Badge> : null}
          </Flex>
          {play && play.sports.length > 0 ? (
            <Flex gap={8} wrap>
              {play.sports.map((sport) => (
                <Badge key={sport} tone="info">
                  {sport}
                  {play.sport_skills[sport] ? ` · ${play.sport_skills[sport]}` : ""}
                </Badge>
              ))}
            </Flex>
          ) : (
            <Text role="small" color="secondary" as="p">
              {play ? "No sports picked yet." : "No Play profile (guest account)."}
            </Text>
          )}
          <Text role="small" color="secondary" as="p">
            Public ID: {user.public_id}
          </Text>
          <Text role="small" color="secondary" as="p">
            Email: {user.email ?? "—"}
          </Text>
          <Text role="small" color="secondary" as="p">
            Phone: {user.phone ?? "—"}
          </Text>
          {play ? (
            <>
              <Text role="small" color="secondary" as="p">
                Location: {play.location || "—"}
              </Text>
              <Text role="small" color="secondary" as="p">
                Joined: {formatDate(play.created_at)}
              </Text>
            </>
          ) : null}
          <Button
            variant="primary"
            onClick={() => {
              onClose();
              void navigate(`/app/users/${user.id}`);
            }}
          >
            Open User Detail
          </Button>
        </Stack>
      ) : null}
    </Drawer>
  );
}
